import React from 'react';
import { useQuery } from '@apollo/client';
import { VEHICLES_LIST } from '../../graphql/vehicles';
import { MAINTENANCE_LIST } from '../../graphql/maintenance';
import { FUEL_LOGS_LIST } from '../../graphql/fuelLogs';
// import { TRIPS_LIST } from '../../graphql/trips';
import { INSPECTIONS_LIST } from '../../graphql/inspections';
import { WORK_ORDERS_LIST } from '../../graphql/workOrders';
import { INSURANCE_POLICIES_LIST } from '../../graphql/insurancePolicies';
import { 
  Add as AddIcon, 
  DirectionsCar, 
  LocalGasStation, 
  Build, 
  Commute,
  Speed,
  TrendingUp,
  Warning,
  CheckCircle,
  Schedule,
  AttachMoney,
  Timeline,
  Security
} from '@mui/icons-material';
import { 
  Button, 
  Stack, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton,
  Avatar,
  Divider,
  LinearProgress
} from '@mui/material';
import { Box, Grid, Card, CardContent, Typography, Chip, CircularProgress } from '@mui/material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import dayjs from 'dayjs';
import VehicleForm from './VehicleForm';
// import TripForm from './TripForm';
import FuelLogForm from './FuelLogForm';
import MaintenanceForm from './MaintenanceForm';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import InspectionForm from './InspectionForm';
import WorkOrderForm from './WorkOrderForm';

const COLORS = ['#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'];

function getVehicleSummary(vehicles) {
  const total = vehicles.length;
  const active = vehicles.filter(v => v.status === 'Active').length;
  const maintenance = vehicles.filter(v => v.status === 'Maintenance').length;
  const assigned = vehicles.filter(v => v.assignedDriver).length;
  return { total, active, maintenance, assigned };
}

function getUpcomingMaintenance(records) {
  const now = dayjs();
  const in7 = now.add(7, 'day');
  
  const upcomingRecords = records.filter(r => {
    const scheduledDate = r.scheduledDate ? dayjs(r.scheduledDate) : null;
    const performedDate = r.performedDate ? dayjs(r.performedDate) : null;
    
    let isUpcoming = false;
    
    if (scheduledDate && scheduledDate.isValid()) {
      // Check if scheduled date is in the next 7 days
      isUpcoming = scheduledDate.isAfter(now) && scheduledDate.isBefore(in7);
    } else if (performedDate && performedDate.isValid()) {
      // Check if performed date is in the next 7 days (for overdue items)
      isUpcoming = performedDate.isAfter(now) && performedDate.isBefore(in7);
    }
    
    // Debug each record
    console.log('Record:', {
      id: r.id,
      scheduledDate: r.scheduledDate,
      performedDate: r.performedDate,
      scheduledDateFormatted: scheduledDate ? scheduledDate.format('YYYY-MM-DD') : 'N/A',
      performedDateFormatted: performedDate ? performedDate.format('YYYY-MM-DD') : 'N/A',
      isUpcoming
    });
    
    return isUpcoming;
  });
  
  return upcomingRecords.length;
}

function getMaintenanceStatusData(records) {
  const pending = records.filter(r => {
    // Maintenance is pending if it has a scheduled date but no performed date
    return r.scheduledDate && !r.performedDate;
  }).length;
  const completed = records.filter(r => {
    // Maintenance is completed if it has a performed date
    return r.performedDate;
  }).length;
  return [
    { name: 'Pending', value: pending },
    { name: 'Completed', value: completed },
  ];
}

function getInsuranceExpirySummary(policies) {
  const now = dayjs();
  const in30 = now.add(30, 'day');
  const in7 = now.add(7, 'day');
  
  
  const expiringIn30Days = policies.filter(policy => {
    if (!policy.renewalDate) return false;
    const renewalDate = dayjs(policy.renewalDate);
    const isExpiring = renewalDate.isAfter(now) && renewalDate.isBefore(in30);
    
    console.log('Policy:', {
      id: policy.id,
      policyNumber: policy.policyNumber,
      provider: policy.provider,
      renewalDate: policy.renewalDate,
      renewalDateFormatted: renewalDate.format('YYYY-MM-DD'),
      daysUntilRenewal: renewalDate.diff(now, 'days'),
      isExpiring
    });
    
    return isExpiring;
  });
  
  const expiringIn7Days = policies.filter(policy => {
    if (!policy.renewalDate) return false;
    const renewalDate = dayjs(policy.renewalDate);
    return renewalDate.isAfter(now) && renewalDate.isBefore(in7);
  });
  
  const expired = policies.filter(policy => {
    if (!policy.renewalDate) return false;
    const renewalDate = dayjs(policy.renewalDate);
    return renewalDate.isBefore(now);
  });
  
  return {
    expiringIn30Days: expiringIn30Days.length,
    expiringIn7Days: expiringIn7Days.length,
    expired: expired.length,
    total: policies.length
  };
}

function getFuelSummary(logs) {
  const now = dayjs();
  const thisMonth = logs.filter(l => dayjs(l.date).isSame(now, 'month'));
  const totalCost = thisMonth.reduce((sum, l) => sum + (l.cost || 0), 0);
  const vehicleMap = {};
  thisMonth.forEach(l => {
    if (!vehicleMap[l.vehicle?.id]) vehicleMap[l.vehicle?.id] = { gallons: 0, count: 0 };
    vehicleMap[l.vehicle?.id].gallons += l.gallons || 0;
    vehicleMap[l.vehicle?.id].count += 1;
  });
  const avgConsumption = Object.values(vehicleMap).length
    ? (Object.values(vehicleMap).reduce((sum, v) => sum + v.gallons, 0) / Object.values(vehicleMap).length).toFixed(2)
    : 0;
  return { totalCost, avgConsumption };
}

// function getTripStats(trips) {
//   const now = dayjs();
//   const thisMonth = trips.filter(t => dayjs(t.startTime).isSame(now, 'month'));
//   const total = thisMonth.length;
//   const avgDistance = total ? (thisMonth.reduce((sum, t) => sum + (t.distance || 0), 0) / total).toFixed(2) : 0;
//   return { total, avgDistance };
// }

// function getTripsPerWeek(trips) {
//   const weekMap = {};
//   trips.forEach(t => {
//     const week = dayjs(t.startTime).format('YYYY-[W]WW');
//     const weekMap[week] = (weekMap[week] || 0) + 1;
//   });
//   return Object.entries(weekMap).map(([week, count]) => ({ week, count }));
// }

function getFuelUsageOverTime(logs) {
  if (!logs || !Array.isArray(logs)) {
    console.log('Invalid fuel logs data:', logs);
    return [];
  }
  
  const dateMap = {};
  logs.forEach(l => {
    if (l && l.date) {
      const date = dayjs(l.date).format('YYYY-MM-DD');
      const gallons = parseFloat(l.gallons) || 0;
      dateMap[date] = (dateMap[date] || 0) + gallons;
    }
  });
  
  const result = Object.entries(dateMap)
    .map(([date, gallons]) => ({ date, gallons }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
    
  console.log('Processed fuel usage data:', result);
  return result;
}

function getRecentActivities(fuelLogs, maintenance, inspections, workOrders) {
  // const tripActs = trips.map(t => ({
  //   type: 'Trip',
  //   date: t.startTime,
  //   desc: `${(t.guard?.items?.[0]?.firstName || '') + ' ' + (t.guard?.items?.[0]?.lastName || '')} drove ${t.vehicle?.make || ''} ${t.vehicle?.model || ''}`.trim(),
  // }));
  const fuelActs = fuelLogs.map(f => ({
    type: 'Fuel',
    date: f.date,
    desc: `${f.driver ? (f.driver.firstName + ' ' + f.driver.lastName) : 'Unknown'} refueled ${f.vehicle?.make || ''} ${f.vehicle?.model || ''}`.trim(),
  }));
  const maintActs = maintenance.map(m => ({
    type: 'Maintenance',
    date: m.performedDate || m.scheduledDate || m.createdAt,
    desc: `${m.description || m._description || 'Maintenance'} for ${m.maintenanceRecords?.make || ''} ${m.maintenanceRecords?.model || ''}`.trim(),
  }));
  const inspectionActs = inspections.map(i => ({
    type: 'Inspection',
    date: i.createdAt,
    desc: `${i.component || 'Inspection'} on ${i.inspections ? (i.inspections.make + ' ' + i.inspections.model) : ''}`.trim(),
  }));
  const workOrderActs = workOrders.map(w => ({
    type: 'Work Order',
    date: w.createdAt,
    desc: `${w._description || 'Work Order'} for ${w.workOrders ? (w.workOrders.make + ' ' + w.workOrders.model) : ''}`.trim(),
  }));
  return [...fuelActs, ...maintActs, ...inspectionActs, ...workOrderActs]
    .filter(a => a.date)
    .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf())
    .slice(0, 10);
}

const StatCard = ({ title, value, subtitle, icon, color, gradient, trend }) => (
  <Card 
    sx={{ 
      height: '100%',
      background: gradient || `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      border: `1px solid ${color}20`,
      borderRadius: 3,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 25px ${color}20`,
      }
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Avatar 
          sx={{ 
            bgcolor: color, 
            width: 48, 
            height: 48,
            boxShadow: `0 4px 12px ${color}40`
          }}
        >
          {icon}
        </Avatar>
        {trend && (
          <Chip 
            label={trend} 
            size="small" 
            color={trend.includes('+') ? 'success' : 'error'}
            sx={{ fontSize: '0.75rem' }}
          />
        )}
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
        {value}
      </Typography>
      <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const QuickActionButton = ({ icon, label, color, onClick }) => (
  <Button
    variant="contained"
    startIcon={icon}
    onClick={onClick}
    sx={{
      background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
      borderRadius: 2,
      px: 3,
      py: 1.5,
      textTransform: 'none',
      fontWeight: 600,
      boxShadow: `0 4px 12px ${color}40`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 6px 20px ${color}60`,
      }
    }}
  >
    {label}
  </Button>
);

const FleetDashboard = () => {
  const { data: vehiclesData, loading: vehiclesLoading, refetch: refetchVehicles } = useQuery(VEHICLES_LIST);
  const { data: maintenanceData, loading: maintenanceLoading, refetch: refetchMaintenance } = useQuery(MAINTENANCE_LIST);
  const { data: fuelLogsData, loading: fuelLogsLoading, refetch: refetchFuelLogs } = useQuery(FUEL_LOGS_LIST);
  // const { data: tripsData, loading: tripsLoading, refetch: refetchTrips } = useQuery(TRIPS_LIST);
  const { data: inspectionsData, loading: inspectionsLoading, refetch: refetchInspections } = useQuery(INSPECTIONS_LIST);
  const { data: workOrdersData, loading: workOrdersLoading, refetch: refetchWorkOrders } = useQuery(WORK_ORDERS_LIST);
  const { data: insurancePoliciesData, loading: insurancePoliciesLoading, refetch: refetchInsurancePolicies } = useQuery(INSURANCE_POLICIES_LIST);

  const [openForm, setOpenForm] = React.useState(null);
  const [formKey, setFormKey] = React.useState(0);

  if (vehiclesLoading || maintenanceLoading || fuelLogsLoading || inspectionsLoading || workOrdersLoading || insurancePoliciesLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '60vh', 
        width: '100%' 
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const vehicles = vehiclesData?.vehiclesList?.items || [];
  const maintenance = maintenanceData?.maintenanceRecordsList?.items || [];
  const fuelLogs = fuelLogsData?.fuelLogsList?.items || [];
  // const trips = tripsData?.tripsList?.items || [];
  const inspections = inspectionsData?.inspectionsList?.items || [];
  const workOrders = workOrdersData?.workOrdersList?.items || [];
  const insurancePolicies = insurancePoliciesData?.insurancePoliciesList?.items || [];

  const vehicleSummary = getVehicleSummary(vehicles);
  
  const upcomingMaintenance = getUpcomingMaintenance(maintenance);
  const maintenanceStatusData = getMaintenanceStatusData(maintenance);
  const fuelSummary = getFuelSummary(fuelLogs);
  const insuranceExpirySummary = getInsuranceExpirySummary(insurancePolicies);
  // const tripStats = getTripStats(trips);
  // const tripsPerWeek = getTripsPerWeek(trips);
  const fuelUsage = getFuelUsageOverTime(fuelLogs);
  const recentActivities = getRecentActivities(fuelLogs, maintenance, inspections, workOrders);

  const handleQuickAction = (type) => {
    setOpenForm(type);
    setFormKey(prev => prev + 1);
  };

  const handleFormClose = (shouldRefetch) => {
    setOpenForm(null);
    if (shouldRefetch) {
      refetchVehicles();
      refetchMaintenance();
      refetchFuelLogs();
      // refetchTrips();
      refetchInspections();
      refetchWorkOrders();
      refetchInsurancePolicies();
    }
  };

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 800, 
            background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1
          }}
        >
          Fleet Dashboard
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
          Monitor and manage your fleet operations in real-time
        </Typography>
      </Box>

      {/* Quick Actions */}
      <Card sx={{ 
        mb: 4, 
        borderRadius: 3, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
            Quick Actions
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <QuickActionButton
              icon={<DirectionsCar />}
              label="Add Vehicle"
              color="#6366f1"
              onClick={() => handleQuickAction('vehicle')}
            />
            {/* <QuickActionButton
              icon={<Commute />}
              label="Add Trip"
              color="#8b5cf6"
              onClick={() => handleQuickAction('trip')}
            /> */}
            <QuickActionButton
              icon={<LocalGasStation />}
              label="Add Fuel Log"
              color="#10b981"
              onClick={() => handleQuickAction('fuel')}
            />
            <QuickActionButton
              icon={<Build />}
              label="Add Maintenance"
              color="#f59e0b"
              onClick={() => handleQuickAction('maintenance')}
            />
            <QuickActionButton
              icon={<CheckCircle />}
              label="Add Inspection"
              color="#06b6d4"
              onClick={() => handleQuickAction('inspection')}
            />
            <QuickActionButton
              icon={<Timeline />}
              label="Add Work Order"
              color="#ef4444"
              onClick={() => handleQuickAction('workOrder')}
            />
      </Stack>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Vehicles"
            value={vehicleSummary.total}
            subtitle={`${vehicleSummary.active} active, ${vehicleSummary.maintenance} in maintenance`}
            icon={<DirectionsCar />}
            color="#6366f1"
            trend="+2 this month"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Upcoming Maintenance"
            value={upcomingMaintenance}
            subtitle="Due within 7 days"
            icon={<Warning />}
            color="#f59e0b"
            trend={upcomingMaintenance > 0 ? `${upcomingMaintenance} urgent` : "All clear"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Insurance Expiring"
            value={insuranceExpirySummary.expiringIn30Days}
            subtitle={`${insuranceExpirySummary.expiringIn7Days} urgent, ${insuranceExpirySummary.expired} expired`}
            icon={<Security />}
            color={insuranceExpirySummary.expiringIn7Days > 0 ? "#ef4444" : "#f59e0b"}
            trend={insuranceExpirySummary.expired > 0 ? `${insuranceExpirySummary.expired} expired` : "All current"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Fuel Cost (This Month)"
            value={`$${fuelSummary.totalCost.toFixed(2)}`}
            subtitle={`${fuelSummary.avgConsumption} gal avg per vehicle`}
            icon={<AttachMoney />}
            color="#10b981"
            trend="+12% vs last month"
          />
        </Grid>
        {/* <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Trips (This Month)"
            value={tripStats.total}
            subtitle={`${tripStats.avgDistance}km average distance`}
            icon={<Speed />}
            color="#8b5cf6"
            trend="+8% vs last month"
          />
        </Grid> */}
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* <Grid item xs={12} lg={6}>
          <Card sx={{ 
            borderRadius: 3, 
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
                Trips Per Week
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={tripsPerWeek}>
                  <XAxis dataKey="week" />
                  <YAxis allowDecimals={false} />
                  <Tooltip 
                    contentStyle={{
                      background: 'rgba(255, 255, 255, 0.95)',
                      border: 'none',
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Bar dataKey="count" fill="url(#tripGradient)" radius={[4, 4, 0, 0]} />
                  <defs>
                    <linearGradient id="tripGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366f1" />
                      <stop offset="100%" stopColor="#8b5cf6" />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid> */}
        <Grid item xs={12} lg={12}>
          <Card sx={{ 
            borderRadius: 3, 
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
                Fuel Usage Over Time
              </Typography>
              {fuelUsage.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={fuelUsage}>
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => dayjs(value).format('MMM DD')}
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      label={{ value: 'Gallons', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip 
                      contentStyle={{
                        background: 'rgba(255, 255, 255, 0.95)',
                        border: 'none',
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                      labelFormatter={(value) => dayjs(value).format('MMM DD, YYYY')}
                      formatter={(value, name) => [`${value} gal`, 'Fuel Usage']}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="gallons" 
                      stroke="url(#fuelGradient)" 
                      strokeWidth={3}
                      dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2 }}
                    />
                    <defs>
                      <linearGradient id="fuelGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#10b981" />
                        <stop offset="100%" stopColor="#06b6d4" />
                      </linearGradient>
                    </defs>
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: 250,
                  color: 'text.secondary'
                }}>
                  <LocalGasStation sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    No fuel data available
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    Add fuel logs to see usage trends
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activities */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ 
            borderRadius: 3, 
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
                Recent Activities
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentActivities.map((a, idx) => (
                      <TableRow key={idx} sx={{ '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.04)' } }}>
                        <TableCell>
                          <Chip 
                            label={a.type} 
                            size="small" 
                            sx={{ 
                              bgcolor: a.type === 'Trip' ? '#6366f1' : 
                                     a.type === 'Fuel' ? '#10b981' : 
                                     a.type === 'Maintenance' ? '#f59e0b' : '#8b5cf6',
                              color: 'white',
                              fontWeight: 500
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{a.desc}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {dayjs(a.date).format('MMM DD, HH:mm')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card sx={{ 
            borderRadius: 3, 
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
                Maintenance Status
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Pending</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{maintenanceStatusData[0]?.value || 0}</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={maintenanceStatusData[0]?.value || 0} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    bgcolor: '#f59e0b20',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: '#f59e0b',
                      borderRadius: 4
                    }
                  }}
                />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Completed</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{maintenanceStatusData[1]?.value || 0}</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={maintenanceStatusData[1]?.value || 0} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    bgcolor: '#10b98120',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: '#10b981',
                      borderRadius: 4
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Form Dialogs */}
      <Dialog open={!!openForm} onClose={handleFormClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {openForm === 'vehicle' && 'Add Vehicle'}
          {/* {openForm === 'trip' && 'Add Trip'} */}
          {openForm === 'fuel' && 'Add Fuel Log'}
          {openForm === 'maintenance' && 'Add Maintenance'}
          {openForm === 'inspection' && 'Add Inspection'}
          {openForm === 'workOrder' && 'Add Work Order'}
        </DialogTitle>
        <DialogContent>
          <div style={{ display: openForm === 'vehicle' ? 'block' : 'none' }}>
            <VehicleForm key={formKey} onClose={handleFormClose} isEditing={true} />
          </div>
          {/* <div style={{ display: openForm === 'trip' ? 'block' : 'none' }}>
            <TripForm key={formKey} onClose={handleFormClose} isEditing={true} />
          </div> */}
          <div style={{ display: openForm === 'fuel' ? 'block' : 'none' }}>
            <FuelLogForm key={formKey} onClose={handleFormClose} isEditing={true} />
          </div>
          <div style={{ display: openForm === 'maintenance' ? 'block' : 'none' }}>
            <MaintenanceForm key={formKey} onClose={handleFormClose} isEditing={true} />
          </div>
          <div style={{ display: openForm === 'inspection' ? 'block' : 'none' }}>
            <InspectionForm key={formKey} onClose={handleFormClose} isEditing={true} />
          </div>
          <div style={{ display: openForm === 'workOrder' ? 'block' : 'none' }}>
            <WorkOrderForm key={formKey} onClose={handleFormClose} isEditing={true} />
          </div>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FleetDashboard;